import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ArrowLeft, CaretDown, CaretUp, Image as ImageIcon, MagnifyingGlass, Printer, Trash, UploadSimple } from "@phosphor-icons/react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { livePhotoUrl, canSeeGT } from "@/lib/fmUtils";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/contexts/AuthContext";

const fmt2 = (v) => (v === null || v === undefined ? "-" : Number(v).toFixed(2));
const fmtPct = (v) => (v === null || v === undefined ? "-" : `${(Number(v) * 100).toFixed(1)}%`);
const fmtInt = (v) => (v === null || v === undefined ? "-" : Math.round(Number(v)));
const fmtDate = (iso) => (iso ? String(iso).slice(0, 10) : "-");

/**
 * Inline editor for the ``matched_item_no`` cell. Admin/Office see a small
 * text input; pressing Enter (or clicking away when the value has changed)
 * PATCHes the backend which re-runs the product-match logic and returns the
 * fully-refreshed row. On success we call ``onSaved`` so the parent can
 * swap the record in place (re-linking the photo, category, gt_price too).
 * On 404 (unknown item no) we surface the backend's precise error message.
 */
function EditableItemNoCell({ record, onSaved, onError }) {
  const original = record.matched_item_no || "";
  const [value, setValue] = useState(original);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setValue(record.matched_item_no || "");
  }, [record.matched_item_no]);

  const commit = async () => {
    const next = value.trim();
    if (next === original.trim()) return;
    setSaving(true);
    try {
      const { data } = await api.patch(`/gt-selling-history/${record.id}`, { matched_item_no: next });
      onSaved(data);
    } catch (e) {
      onError(e);
      setValue(original); // roll back input to previous value on failure
    } finally {
      setSaving(false);
    }
  };

  return (
    <Input
      value={value}
      onChange={(e) => setValue(e.target.value)}
      onKeyDown={(e) => {
        if (e.key === "Enter") {
          e.currentTarget.blur();
        } else if (e.key === "Escape") {
          setValue(original);
          e.currentTarget.blur();
        }
      }}
      onBlur={commit}
      disabled={saving}
      placeholder="Item No"
      className="h-7 w-28 rounded-sm border-zinc-300 px-1.5 text-sm focus-visible:ring-2 focus-visible:ring-blue-600"
      data-testid={`gt-history-item-no-input-${record.id}`}
    />
  );
}

const COLUMNS = [
  { key: "vendor_part", label: "Vendor Part #", type: "text" },
  { key: "gt_sku", label: "GT SKU", type: "text" },
  { key: "matched_item_no", label: "Item No", type: "text" },
  { key: "category", label: "Category", type: "text" },
  { key: "buyer", label: "Buyer", type: "text" },
  { key: "description", label: "Description", type: "text" },
  { key: "chain_retail", label: "Chain Retail", type: "number" },
  { key: "gt_price", label: "GT Price", type: "number" },
  { key: "bh_price", label: "Price", type: "number" },
  { key: "lw_ros", label: "LW ROS", type: "number" },
  { key: "lw_sales_units", label: "LW Sales Units", type: "number" },
  { key: "md", label: "MD", type: "number" },
  { key: "store_oh", label: "Store OH", type: "number" },
  { key: "imported_at", label: "Sales Date", type: "text" },
];

export default function GTSellingHistory() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { user } = useAuth();
  const canManage = user && (user.role === "admin" || user.role === "office");
  const gtAllowed = canSeeGT(user);
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [buyerFilter, setBuyerFilter] = useState("all");
  const [selectedIds, setSelectedIds] = useState(() => {
    try {
      const raw = sessionStorage.getItem("gtHistorySelectedIds");
      return raw ? JSON.parse(raw) : {};
    } catch {
      return {};
    }
  });
  const [sortKey, setSortKey] = useState("lw_ros");
  const [sortDir, setSortDir] = useState("desc");

  // Persist row-selection to sessionStorage so ticks survive the Print
  // navigation round-trip (component unmounts on route change).
  useEffect(() => {
    try {
      sessionStorage.setItem("gtHistorySelectedIds", JSON.stringify(selectedIds));
    } catch {
      /* ignore */
    }
  }, [selectedIds]);

  const fetchRecords = () => {
    api
      .get("/gt-selling-history")
      .then(({ data }) => setRecords(data.records))
      .catch(() => toast({ title: "Could not load GT Selling History", variant: "destructive" }))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (user && !gtAllowed) {
      navigate("/", { replace: true });
      return;
    }
    fetchRecords();
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleDelete = async (id) => {
    try {
      await api.delete(`/gt-selling-history/${id}`);
      setRecords((prev) => prev.filter((r) => r.id !== id));
    } catch (e) {
      toast({ title: "Delete failed", description: formatApiErrorDetail(e.response?.data?.detail), variant: "destructive" });
    }
  };

  const [importingGT, setImportingGT] = useState(false);
  // Sales-date the user MUST type before picking a file. Kept empty on load so
  // buyers are forced to enter the correct sales-week date every time and never
  // accidentally import a fresh report under today's date.
  const [importSalesDate, setImportSalesDate] = useState("");
  const gtFileInputRef = useRef(null);
  const handleImportGTFile = async (file) => {
    if (!importSalesDate) {
      toast({
        title: "Sales Date required",
        description: "Enter the sales week date before importing the .xlsx.",
        variant: "destructive",
      });
      return;
    }
    setImportingGT(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("sales_date", importSalesDate);
      const { data } = await api.post("/gt-selling-history/import", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast({
        title: "GT report imported",
        description: `${data.imported} new, ${data.updated} updated, ${data.matched} matched to products, ${data.skipped} rows skipped by filter.`,
      });
      fetchRecords();
    } catch (e) {
      toast({
        title: "Import failed",
        description: formatApiErrorDetail(e.response?.data?.detail),
        variant: "destructive",
      });
    }
    setImportingGT(false);
  };

  const handleSort = (key) => {
    if (sortKey === key) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else {
      setSortKey(key);
      setSortDir(COLUMNS.find((c) => c.key === key)?.type === "number" ? "desc" : "asc");
    }
  };

  const buyerOptions = useMemo(() => {
    const set = new Set();
    for (const r of records) {
      const b = (r.buyer || "").trim();
      if (b) set.add(b);
    }
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [records]);

  const visibleRecords = useMemo(() => {
    let list = records;
    if (buyerFilter !== "all") {
      list = list.filter((r) => (r.buyer || "").trim() === buyerFilter);
    }
    const q = search.trim().toLowerCase();
    if (q) {
      list = list.filter((r) =>
        ["vendor_part", "gt_sku", "matched_item_no", "category", "buyer", "description"].some((k) =>
          String(r[k] || "").toLowerCase().includes(q)
        )
      );
    }
    if (dateFrom) list = list.filter((r) => fmtDate(r.imported_at) >= dateFrom);
    if (dateTo) list = list.filter((r) => fmtDate(r.imported_at) <= dateTo);
    const type = COLUMNS.find((c) => c.key === sortKey)?.type;
    const dir = sortDir === "asc" ? 1 : -1;
    return [...list].sort((a, b) => {
      const av = a[sortKey];
      const bv = b[sortKey];
      if (av === null || av === undefined || av === "") return 1;
      if (bv === null || bv === undefined || bv === "") return -1;
      if (type === "number") return (Number(av) - Number(bv)) * dir;
      return String(av).localeCompare(String(bv)) * dir;
    });
  }, [records, buyerFilter, search, dateFrom, dateTo, sortKey, sortDir]);

  const selectedCount = Object.values(selectedIds).filter(Boolean).length;

  const handleToggleSelect = (id) => {
    setSelectedIds((prev) => {
      const next = { ...prev };
      if (next[id]) delete next[id];
      else next[id] = true;
      return next;
    });
  };

  const handleToggleSelectAllVisible = () => {
    const allSelected = visibleRecords.every((r) => selectedIds[r.id]);
    setSelectedIds((prev) => {
      const next = { ...prev };
      for (const r of visibleRecords) {
        if (allSelected) delete next[r.id];
        else next[r.id] = true;
      }
      return next;
    });
  };

  const handlePrintSelected = () => {
    const chosen = visibleRecords.filter((r) => selectedIds[r.id]);
    if (chosen.length === 0) {
      toast({ title: "No rows selected", description: "Tick at least one row to print.", variant: "destructive" });
      return;
    }
    sessionStorage.setItem("gtHistoryPrintRecords", JSON.stringify(chosen));
    navigate("/gt-selling-history/print");
  };

  return (
    <div className="flex h-screen w-screen flex-col bg-white" data-testid="gt-selling-history-page">
      <header className="flex flex-wrap items-center gap-3 border-b border-zinc-300 px-4 py-3">
        <Button
          variant="outline"
          size="sm"
          className="rounded-sm border-zinc-300"
          onClick={() => navigate("/")}
          data-testid="gt-history-back-button"
        >
          <ArrowLeft size={14} className="mr-1.5" /> Back
        </Button>
        <h1 className="text-base font-bold text-zinc-900">GT Selling History</h1>
        <span className="text-xs text-zinc-500" data-testid="gt-history-count">
          {visibleRecords.length} / {records.length} records
        </span>
        <div className="ml-auto flex items-center gap-2">
          {canManage && (
            <>
              <input
                ref={gtFileInputRef}
                type="file"
                accept=".xlsx"
                className="hidden"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) handleImportGTFile(f);
                  e.target.value = "";
                }}
                data-testid="gt-import-file-input"
              />
              <div className="flex items-center gap-1" title="Sales week the report data belongs to (required before Import)">
                <span className="text-xs text-zinc-500">Sales Date</span>
                <Input
                  type="date"
                  value={importSalesDate}
                  onChange={(e) => setImportSalesDate(e.target.value)}
                  className="h-9 w-[8.5rem] rounded-sm border-zinc-300 text-sm focus-visible:ring-2 focus-visible:ring-blue-600"
                  data-testid="gt-import-sales-date-input"
                />
              </div>
              <Button
                variant="outline"
                size="sm"
                className="rounded-sm border-zinc-300"
                onClick={() => gtFileInputRef.current?.click()}
                disabled={importingGT || !importSalesDate}
                title="Import a GT Sell-Through Report (.xlsx). Only rows with LW ROS > 0.11, MD < 0.05 and Store OH > 200 are imported."
                data-testid="gt-import-button"
              >
                <UploadSimple size={14} className={`mr-1.5 ${importingGT ? "animate-pulse" : ""}`} />
                {importingGT ? "Importing..." : "Import"}
              </Button>
            </>
          )}
          <div className="flex items-center gap-1" title="Filter by Buyer">
            <span className="text-xs text-zinc-500">Buyer</span>
            <Select value={buyerFilter} onValueChange={setBuyerFilter}>
              <SelectTrigger
                className="h-9 w-[10rem] rounded-sm border-zinc-300 text-sm focus-visible:ring-2 focus-visible:ring-blue-600"
                data-testid="gt-history-buyer-filter"
              >
                <SelectValue placeholder="All buyers" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All buyers</SelectItem>
                {buyerOptions.map((b) => (
                  <SelectItem key={b} value={b} data-testid={`gt-history-buyer-option-${b}`}>
                    {b}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center gap-1" title="Filter by Sales Date range">
            <span className="text-xs text-zinc-500">Sales Date</span>
            <Input
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              className="w-[8.5rem] rounded-sm border-zinc-300 text-sm focus-visible:ring-2 focus-visible:ring-blue-600"
              data-testid="gt-history-date-from-input"
            />
            <span className="text-xs text-zinc-400">to</span>
            <Input
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              className="w-[8.5rem] rounded-sm border-zinc-300 text-sm focus-visible:ring-2 focus-visible:ring-blue-600"
              data-testid="gt-history-date-to-input"
            />
          </div>
          <div className="relative w-64">
            <MagnifyingGlass size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-zinc-400" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search part, SKU, buyer, description..."
              className="rounded-sm border-zinc-300 pl-8 text-sm focus-visible:ring-2 focus-visible:ring-blue-600"
              data-testid="gt-history-search-input"
            />
          </div>
          <Button
            size="sm"
            className="rounded-sm bg-blue-600 text-white hover:bg-blue-700"
            onClick={handlePrintSelected}
            disabled={selectedCount === 0}
            data-testid="gt-history-print-button"
          >
            <Printer size={14} className="mr-1.5" />
            Print{selectedCount > 0 ? ` (${selectedCount})` : ""}
          </Button>
        </div>
      </header>

      <div className="min-h-0 flex-1 overflow-auto">
        {loading ? (
          <p className="p-6 text-sm text-zinc-500">Loading...</p>
        ) : visibleRecords.length === 0 ? (
          <p className="p-6 text-sm text-zinc-500" data-testid="gt-history-empty">
            {records.length === 0
              ? "No records yet. Use the Import button above to import a GT Sell-Through Report."
              : "No records match your search."}
          </p>
        ) : (
          <Table data-testid="gt-history-table">
            <TableHeader className="sticky top-0 bg-zinc-50">
              <TableRow className="border-b border-zinc-300">
                <TableHead className="w-8 py-1.5">
                  <Checkbox
                    checked={
                      visibleRecords.length > 0 && visibleRecords.every((r) => selectedIds[r.id])
                    }
                    onCheckedChange={handleToggleSelectAllVisible}
                    data-testid="gt-history-select-all-checkbox"
                  />
                </TableHead>
                <TableHead className="text-xs uppercase tracking-[0.05em] text-zinc-500">Photo</TableHead>
                {COLUMNS.map((c) => (
                  <TableHead
                    key={c.key}
                    onClick={() => handleSort(c.key)}
                    className="cursor-pointer select-none whitespace-nowrap text-xs uppercase tracking-[0.05em] text-zinc-500 hover:text-blue-600"
                    data-testid={`gt-history-sort-${c.key}`}
                  >
                    <span className="inline-flex items-center gap-1">
                      {c.label}
                      {sortKey === c.key &&
                        (sortDir === "asc" ? <CaretUp size={11} weight="bold" /> : <CaretDown size={11} weight="bold" />)}
                    </span>
                  </TableHead>
                ))}
                <TableHead />
              </TableRow>
            </TableHeader>
            <TableBody>
              {visibleRecords.map((r) => (
                <TableRow key={r.id} className="border-b border-zinc-200" data-testid={`gt-history-row-${r.id}`}>
                  <TableCell className="py-1.5">
                    <Checkbox
                      checked={!!selectedIds[r.id]}
                      onCheckedChange={() => handleToggleSelect(r.id)}
                      data-testid={`gt-history-row-checkbox-${r.id}`}
                    />
                  </TableCell>
                  <TableCell className="py-1.5">
                    {r.matched_record_id ? (
                      <img
                        src={livePhotoUrl("main products", r.matched_record_id, "photo")}
                        alt="product"
                        className="h-14 w-14 rounded-sm border border-zinc-200 bg-zinc-50 object-contain"
                        loading="lazy"
                        onError={(e) => {
                          e.target.style.display = "none";
                        }}
                      />
                    ) : (
                      <div className="flex h-14 w-14 items-center justify-center rounded-sm border border-zinc-200 bg-zinc-50">
                        <ImageIcon size={16} className="text-zinc-300" />
                      </div>
                    )}
                  </TableCell>
                  <TableCell className="whitespace-nowrap py-1.5 text-sm font-semibold text-zinc-900">{r.vendor_part}</TableCell>
                  <TableCell className="whitespace-nowrap py-1.5 font-mono-data text-sm text-zinc-800">{r.gt_sku || "-"}</TableCell>
                  <TableCell className="whitespace-nowrap py-1.5 text-sm text-zinc-600" data-testid={`gt-history-item-no-cell-${r.id}`}>
                    {canManage ? (
                      <EditableItemNoCell
                        record={r}
                        onSaved={(fresh) => {
                          setRecords((prev) => prev.map((row) => (row.id === fresh.id ? fresh : row)));
                          toast({
                            title: fresh.matched_item_no
                              ? `Linked to ${fresh.matched_item_no}`
                              : "Cleared item-no link",
                          });
                        }}
                        onError={(e) =>
                          toast({
                            title: "Could not link item",
                            description: formatApiErrorDetail(e.response?.data?.detail),
                            variant: "destructive",
                          })
                        }
                      />
                    ) : (
                      r.matched_item_no || "-"
                    )}
                  </TableCell>
                  <TableCell className="whitespace-nowrap py-1.5 text-sm text-zinc-600" data-testid={`gt-history-category-${r.id}`}>{r.category || "-"}</TableCell>
                  <TableCell className="py-1.5 text-sm text-zinc-600">{r.buyer || "-"}</TableCell>
                  <TableCell className="max-w-[280px] py-1.5 text-sm text-zinc-800">{r.description || "-"}</TableCell>
                  <TableCell className="py-1.5 text-right font-mono-data text-sm">{fmt2(r.chain_retail)}</TableCell>
                  <TableCell className="py-1.5 text-right font-mono-data text-sm">{fmt2(r.gt_price)}</TableCell>
                  <TableCell
                    className={`py-1.5 text-right font-mono-data text-sm ${
                      r.bh_price != null && r.gt_price
                        ? Math.abs(r.bh_price - r.gt_price) / r.gt_price > 0.12
                          ? "font-semibold text-red-600"
                          : "text-zinc-800"
                        : "text-zinc-800"
                    }`}
                    title={
                      r.bh_price != null && r.gt_price
                        ? `${(((r.bh_price - r.gt_price) / r.gt_price) * 100).toFixed(1)}% vs GT Price`
                        : undefined
                    }
                    data-testid={`gt-history-bh-price-${r.id}`}
                  >
                    {fmt2(r.bh_price)}
                  </TableCell>
                  <TableCell className="py-1.5 text-right font-mono-data text-sm">{fmtPct(r.lw_ros)}</TableCell>
                  <TableCell className="py-1.5 text-right font-mono-data text-sm">{fmtInt(r.lw_sales_units)}</TableCell>
                  <TableCell className="py-1.5 text-right font-mono-data text-sm">{fmtPct(r.md)}</TableCell>
                  <TableCell className="py-1.5 text-right font-mono-data text-sm">{fmtInt(r.store_oh)}</TableCell>
                  <TableCell className="whitespace-nowrap py-1.5 text-sm text-zinc-600" data-testid={`gt-history-sales-date-${r.id}`}>
                    {fmtDate(r.imported_at)}
                  </TableCell>
                  <TableCell className="py-1.5 text-right">
                    {canManage && (
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-7 w-7 p-0 text-zinc-400 hover:text-red-600"
                        onClick={() => handleDelete(r.id)}
                        data-testid={`gt-history-delete-${r.id}`}
                      >
                        <Trash size={14} />
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>
    </div>
  );
}
